import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Space, Tag, Typography, message,
} from 'antd';
import { CloudSyncOutlined, DeleteOutlined } from '@ant-design/icons';
import type { MarkAttendanceRequest } from '@velora/contracts';
import {
  outboxDrain, outboxList, outboxRemove, outboxSubscribe, type OutboxItem,
} from '@velora/sync';
import { productApi } from '../../api';

/**
 * Offline queue view. Shows queued mutations (O-02) and offers a Drain button
 * when the network is back. Attendance is the only op today; adding new ops
 * is a matter of adding a drainer here.
 */
export function TeacherOfflineQueuePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [items, setItems] = useState<OutboxItem[]>(outboxList());
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [busy, setBusy] = useState(false);

  useEffect(() => outboxSubscribe(() => setItems(outboxList())), []);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const drain = async () => {
    setBusy(true);
    try {
      const r = await outboxDrain<{ institution: string; payload: MarkAttendanceRequest }>(
        'attendance.mark',
        ({ institution: slug, payload }) => productApi.markAttendance(slug, payload).then(() => undefined),
      );
      setItems(outboxList());
      if (r.sent > 0) void message.success(`Synced ${r.sent} · ${r.failed} still queued`);
      else if (r.failed > 0) void message.error('Sync failed — still queued');
      else void message.info('Nothing to sync');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Offline queue</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          O-02 · marks made while offline sit here until the network returns.
        </Typography.Text>
      </div>

      <Alert
        type={online ? 'success' : 'warning'}
        showIcon
        message={online ? 'Online' : 'Offline'}
        description={online
          ? 'Tap "Sync now" to push queued marks. Marks are preserved in order.'
          : 'When your device reconnects, come back here and press Sync.'}
        action={<Button type="primary" icon={<CloudSyncOutlined />} onClick={drain} loading={busy} disabled={!online || items.length === 0}>
          Sync now
        </Button>}
      />

      {items.length === 0 ? (
        <Empty description="Nothing queued." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map((it) => <QueueItem key={it.id} it={it} onRemove={() => { outboxRemove(it.id); setItems(outboxList()); }} />)}
        </div>
      )}

      <Card size="small" title="Where does this appear?">
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#3f4a63' }}>
          <li>Attendance saved from the mobile register (<code>/teacher/attendance/*</code>) queues automatically when offline or when a POST fails.</li>
          <li>Marks are attempted in the order they were made, so re-submits can't accidentally overwrite newer edits.</li>
          <li>Failed items keep their attempt count and last error — you can safely leave the tab open.</li>
        </ul>
      </Card>
    </Space>
  );
}

function QueueItem({ it, onRemove }: { it: OutboxItem; onRemove: () => void }) {
  const p = it.payload as { institution?: string; payload?: MarkAttendanceRequest } | undefined;
  const req = p?.payload;
  return (
    <Card size="small">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <Tag color="processing">{it.op}</Tag>
        <span style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
          {new Date(it.created_at).toLocaleString()}
        </span>
        <div style={{ flex: 1 }} />
        {it.attempts > 0 && <Tag color="warning">{it.attempts} attempt{it.attempts === 1 ? '' : 's'}</Tag>}
      </div>
      {req && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          <b>{req.grade}{req.section ? `-${req.section}` : ''}</b> · {req.date} · {req.entries.length} mark{req.entries.length === 1 ? '' : 's'}
          {req.submit && <> · submitting</>}
        </div>
      )}
      {it.last_error && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#a24040' }}>Last error: {it.last_error}</div>
      )}
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Button size="small" icon={<DeleteOutlined />} danger onClick={onRemove}>Discard</Button>
      </div>
    </Card>
  );
}
